/**
 * Outermost wrapper component for the application
 */
import { create } from 'jss';
import React from 'react';
import PropTypes from 'prop-types';
import { Router } from 'react-router-dom';
import expand from 'jss-expand';
import {
  StylesProvider,
  ThemeProvider,
  withStyles,
  jssPreset,
  createGenerateClassName,
} from '@material-ui/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { StripeProvider } from 'react-stripe-elements';
import { SnackbarProvider } from 'notistack';
import { ErrorBoundary } from 'react-error-boundary';

import SnackbarNotifier from 'components/SnackNotifier/SnackbarNotifierContainer';
import ErrorBoundaryFallback from 'components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { history } from '../../store';
import RoutesContainer from './Routes/RoutesContainer';
import FeatureFlagsContainer from './FeatureFlags/FeatureFlagsContainer';
import UpdateSessionContainer from './UpdateSession/UpdateSessionContainer';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import { darkTheme } from './App.theme';

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, expand()] });

// Custom Material-UI class name generator.
const generateClassName = createGenerateClassName();

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  org: PropTypes.shape({
    id: PropTypes.string,
    billingSetupComplete: PropTypes.bool,
    isLoading: PropTypes.bool,
  }).isRequired,
  getOrg: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getServices: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getProviders: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getVersions: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getRegions: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getPlans: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getFlavors: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  getAddons: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  needsOrganization: PropTypes.bool.isRequired, // eslint-disable-line react/no-unused-prop-types
};

export class UnstyledApp extends React.Component {
  static onUserDataLoaded(props) {
    if (!props.needsOrganization && !props.org.isLoading) {
      props.getOrg();
    }
    props.getServices();
    props.getServiceSubtypes();
    props.getProviders();
    props.getVersions();
    props.getRegions();
    props.getPlans();
    props.getFlavors();
    props.getAddons();
  }

  componentDidMount() {
    // Ensure user data has finished loading
    const { user } = this.props;
    if (user.id) {
      UnstyledApp.onUserDataLoaded(this.props);
    }
  }

  componentDidUpdate(prevProps) {
    const { user } = this.props;
    // Check if user data went from not loaded -> loaded
    if (!prevProps.user.id && user.id) {
      UnstyledApp.onUserDataLoaded(this.props);
    }
  }

  render() {
    const { org, classes } = this.props;
    const routes = !org.isLoading ? (
      <Router history={history}>
        <RoutesContainer />
      </Router>
    ) : (
      <LoadingPlaceholder />
    );
    return (
      <React.Fragment>
        <UpdateSessionContainer />
        <FeatureFlagsContainer />
        <StripeProvider apiKey={process.env.REACT_APP_STRIPE_KEY}>
          <StylesProvider jss={jss} generateClassName={generateClassName}>
            <ThemeProvider theme={darkTheme}>
              <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
                <SnackbarProvider
                  maxSnack={3}
                  classes={{
                    variantSuccess: classes.success,
                    variantError: classes.error,
                  }}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                >
                  <SnackbarNotifier />
                  <CssBaseline />
                  {routes}
                </SnackbarProvider>
              </ErrorBoundary>
            </ThemeProvider>
          </StylesProvider>
        </StripeProvider>
      </React.Fragment>
    );
  }
}

UnstyledApp.propTypes = propTypes;

const styles = () => ({
  '@global': {
    html: {
      height: '100%',
    },
    body: {
      height: '100%',
    },
    '#root': {
      height: '100%',
    },
  },
  success: {
    backgroundColor: '#3C9BA0 !important',
    color: 'white !important',
  },
  error: {
    color: 'white !important',
  },
});

export default withStyles(styles)(UnstyledApp);
